import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMapTo, tap } from 'rxjs/operators';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { FunnelService } from './funnel.service';
import { LoopService } from '../loop/loop.service';
import {
  cloneLoopConfig, DEFAULT_LOOP_CONFIG, LoopConfig, readLoopConfig, sameLoopConfig, validateLoopConfig
} from '../loop/loop.logic';
import {
  cloneConfig, defaultFunnel, FunnelConfig, outcomeNames, readFunnel, sameFunnel, unmappedOutcomes, validateFunnel
} from './funnel.logic';

/** Which part of the project settings a host shows: the funnel, the loop, or both stacked. */
export type SettingsMode = 'funnel' | 'loop' | 'both';

/**
 * Project settings: the funnel builder and the loop settings, each with its own draft
 * and save so saving one never overwrites the other. The workflow stepper shows the
 * funnel in its Funnel step and the loop in Goals, KPIs & Loops; project edit can show both.
 */
@Component({
  selector: 'hour4u-admin-project-settings',
  templateUrl: './project-settings.component.html',
  styleUrls: ['./project-settings.component.scss']
})
export class ProjectSettingsComponent implements OnChanges {
  @Input() projectDetails: any;
  @Input() mode: SettingsMode = 'both';
  /** Emits after a successful save so the host can reload the project. */
  @Output() saved = new EventEmitter<void>();

  profile = this.appService.getCurrentAccount();

  draft: FunnelConfig = defaultFunnel();
  /** What is stored on the project (null when no funnel has been saved). */
  stored: FunnelConfig | null = null;
  isSaving = false;
  saveError = '';

  loopDraft: LoopConfig = cloneLoopConfig(DEFAULT_LOOP_CONFIG);
  loopStored: LoopConfig = cloneLoopConfig(DEFAULT_LOOP_CONFIG);
  isSavingLoop = false;
  loopSaveError = '';

  constructor(
    private funnelService: FunnelService,
    private loopService: LoopService,
    private appService: AppService,
    private notify: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['projectDetails'];
    if (!change) { return; }
    const before = change.previousValue;
    const after = change.currentValue;
    // Keep unsaved edits when the same project object is refreshed in the background.
    const sameProject = before && after && before.project_id === after.project_id;
    if (!sameProject || !this.isDirty) {
      this.loadFromProject();
    }
    if (!sameProject || !this.isLoopDirty) {
      this.loadLoopFromProject();
    }
  }

  get showFunnel(): boolean { return this.mode !== 'loop'; }
  get showLoop(): boolean { return this.mode !== 'funnel'; }

  private loadFromProject() {
    this.stored = readFunnel(this.projectDetails);
    this.draft = this.stored ? cloneConfig(this.stored) : defaultFunnel();
    this.saveError = '';
  }

  private loadLoopFromProject() {
    this.loopStored = readLoopConfig(this.projectDetails);
    this.loopDraft = cloneLoopConfig(this.loopStored);
    this.loopSaveError = '';
  }

  // Funnel
  get outcomes(): string[] {
    return outcomeNames(this.projectDetails);
  }

  get unmapped(): string[] {
    return unmappedOutcomes(this.draft, this.outcomes);
  }

  get errors(): string[] {
    return validateFunnel(this.draft);
  }

  /** True when there is something to save: edits, or a funnel that was never saved. */
  get isDirty(): boolean {
    return !this.stored || !sameFunnel(this.stored, this.draft);
  }

  /** The funnel can be saved and moved past: it is valid and every outcome sits in a stage. */
  get funnelReady(): boolean {
    return this.errors.length === 0 && this.unmapped.length === 0;
  }

  get canSave(): boolean {
    return this.isDirty && this.funnelReady && !this.isSaving;
  }

  onFunnelChange(config: FunnelConfig) {
    this.draft = config;
  }

  discard() {
    this.loadFromProject();
  }

  /** Saves the funnel (a no-op when nothing changed). Completes once stored; errors after showing the message. */
  saveFunnel(): Observable<void> {
    if (!this.profile || !this.projectDetails) { return throwError(new Error('No project')); }
    if (!this.isDirty) { return of(undefined); }
    this.isSaving = true;
    this.saveError = '';
    const saving = cloneConfig(this.draft);
    return this.funnelService.saveFunnel(this.profile, this.projectDetails.project_id, saving).pipe(
      tap(() => {
        this.isSaving = false;
        this.notify.showMessage('Funnel saved', 2000);
        this.stored = saving;
        this.saved.emit();
      }),
      map(() => undefined),
      catchError((err) => {
        this.isSaving = false;
        const message = err && err.error && err.error.message;
        this.saveError = message || 'Could not save the funnel. Your changes are still here, try again.';
        return throwError(err);
      })
    );
  }

  save() {
    if (this.canSave) {
      this.saveFunnel().subscribe(() => {}, () => {});
    }
  }

  // Cost per goal target (kept on the funnel config, edited next to the loop in Goals, KPIs & Loops)
  get targetValue(): number | null {
    const t = this.draft.target_cost_per_goal;
    return typeof t === 'number' ? t : null;
  }

  setTarget(value: any) {
    const n = value === '' || value === null || value === undefined ? null : Number(value);
    const next = cloneConfig(this.draft);
    if (n === null || !isFinite(n)) {
      delete next.target_cost_per_goal;
    } else {
      next.target_cost_per_goal = n;
    }
    this.draft = next;
  }

  // Loop
  get loopErrors(): string[] {
    return validateLoopConfig(this.loopDraft);
  }

  get loopReady(): boolean {
    return this.loopErrors.length === 0 && this.targetErrors.length === 0;
  }

  get targetErrors(): string[] {
    return validateFunnel(this.draft).filter(e => e.indexOf('cost per goal target') !== -1);
  }

  get isLoopDirty(): boolean {
    return !sameLoopConfig(this.loopStored, this.loopDraft) || (this.showLoop && !this.showFunnel && this.targetDirty);
  }

  /** The target lives on the funnel config; a loop-only host saves it together with the loop. */
  private get targetDirty(): boolean {
    const stored = this.stored ? this.stored.target_cost_per_goal : undefined;
    return !!this.stored && stored !== this.draft.target_cost_per_goal;
  }

  get canSaveLoop(): boolean {
    return this.isLoopDirty && this.loopReady && !this.isSavingLoop;
  }

  onLoopChange(config: LoopConfig) {
    this.loopDraft = config;
  }

  discardLoop() {
    this.loadLoopFromProject();
    this.loadFromProject();
  }

  /** Saves the loop settings (and the cost per goal target when this host owns it). A no-op when nothing changed. */
  saveLoop(): Observable<void> {
    if (!this.profile || !this.projectDetails) { return throwError(new Error('No project')); }
    const saveTarget = !this.showFunnel && this.targetDirty;
    const saveLoopConfig = !sameLoopConfig(this.loopStored, this.loopDraft);
    if (!saveTarget && !saveLoopConfig) { return of(undefined); }
    this.isSavingLoop = true;
    this.loopSaveError = '';
    const saving = cloneLoopConfig(this.loopDraft);
    const target = cloneConfig(this.draft);
    const projectId = this.projectDetails.project_id;
    const targetCall: Observable<any> = saveTarget ? this.funnelService.saveFunnel(this.profile, projectId, target) : of(null);
    const loopCall: Observable<any> = saveLoopConfig ? this.loopService.saveLoopConfig(this.profile, projectId, saving) : of(null);
    return targetCall.pipe(
      tap(() => { if (saveTarget) { this.stored = target; } }),
      switchMapTo(loopCall),
      tap(() => {
        this.isSavingLoop = false;
        this.loopStored = saving;
        this.notify.showMessage('Loop settings saved', 2000);
        this.saved.emit();
      }),
      map(() => undefined),
      catchError((err) => {
        this.isSavingLoop = false;
        const message = err && err.error && err.error.message;
        this.loopSaveError = message || 'Could not save the loop settings. Your changes are still here, try again.';
        return throwError(err);
      })
    );
  }

  saveLoopClick() {
    if (this.canSaveLoop) {
      this.saveLoop().subscribe(() => {}, () => {});
    }
  }
}
